import { useImportColumnsLazyQuery, type ImportEntity } from '@paintscout/api'
import { Alert, Dropzone, makeStyles, Spinner, Typography, UploadIcon } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import classnames from 'classnames'
import React, { useRef, useState } from 'react'
import { getImportErrorMessage } from '../importErrorMessages'
import { IMPORT_MAX_INLINE_BYTES, IMPORT_MAX_INLINE_LABEL } from '../importLimits'
import type { ImportColumnsResult, ImportWizardFile } from '../useImportWizard'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    // Stretch to the bottom of the dialog body so the dashed dropzone below can fill it.
    flex: 1,
    minHeight: 0
  },
  // The shared Dropzone draws no border of its own, so the dashed drop target is defined here.
  // `flex: 1` lets it fill whatever height the dialog body has left, as in the mock.
  dropzone: {
    flex: 1,
    minHeight: 240,
    // Centres the preview itself rather than leaving it to a percentage height on the child, which
    // does not resolve inside a `flex: 1` parent and left the icon sitting near the top.
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    border: `1px dashed ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: theme.borderRadius.xl,
    backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
    // The whole area is the click target (react-dropzone opens the picker from the root), so it
    // reads as one. react-dropzone drops the click handler while `disabled`, hence the reset.
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.paintscout.palette.brand.Neutral.White
      }
    }
  },
  dropzoneDisabled: {
    cursor: 'default',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P85,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P98
    }
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    textAlign: 'center'
  },
  // Rounded blue tile behind the upload glyph.
  iconTile: {
    width: 64,
    height: 64,
    borderRadius: theme.borderRadius.xl,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.paintscout.palette.secondary[260].C95,
    marginBottom: theme.spacing(2)
  },
  icon: {
    fontSize: 28,
    color: theme.palette.primary.main
  },
  // The prompt sits a step above body copy in size and weight, as in the mock. `em`, not `rem`,
  // so both lines still follow the theme's fontScale (body2 is deliberately the same size as
  // body1 in this theme, so the variants alone cannot make the step).
  prompt: {
    fontSize: '1.0625em',
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary
  },
  browse: {
    color: theme.palette.primary.main
  },
  helper: {
    fontSize: '0.875em',
    color: theme.palette.text.secondary
  }
}))

export const CSV_ACCEPT = 'text/csv,.csv'

export const WRONG_TYPE_MESSAGE = 'Only .csv files can be imported. Export your spreadsheet as CSV and try again.'
export const TOO_BIG_MESSAGE = `That file is over ${IMPORT_MAX_INLINE_LABEL}. Split it into smaller files.`

export interface UploadStepProps {
  entity: ImportEntity
  onParsed: (file: ImportWizardFile, result: ImportColumnsResult) => void
}

function isCsv(file: File): boolean {
  // Browsers report an empty or odd `type` for CSV often enough that the extension is the
  // reliable check (see Dropzone.tsx's own note on attr-accept).
  return /\.csv$/i.test(file.name) || file.type === 'text/csv'
}

/**
 * Step 1: choose a CSV. The file is read in the browser and posted inline as `source.csv`;
 * the server writes it to S3 and returns the `fileKey` every later call uses.
 */
export default function UploadStep({ entity, onParsed }: UploadStepProps) {
  const classes = useStyles()
  const [localError, setLocalError] = useState<string | undefined>()
  const [reading, setReading] = useState(false)
  const pendingFile = useRef<ImportWizardFile | undefined>()

  const [runImportColumns, { loading: queryLoading, error }] = useImportColumnsLazyQuery({
    fetchPolicy: 'no-cache', // has a side effect: writes S3 and mints a fileKey
    onCompleted: (data) => {
      setReading(false)
      const result = data?.importColumns
      if (!result) {
        return
      }
      onParsed(pendingFile.current ?? { name: '', size: 0 }, {
        fileKey: result.fileKey,
        rowCount: result.rowCount,
        matchedCount: result.matchedCount,
        columns: result.columns ?? [],
        fields: result.fields ?? []
      })
    },
    onError: () => setReading(false)
  })

  const loading = reading || queryLoading

  async function handleDrop(files: File[]) {
    const file = files?.[0]
    if (!file) {
      return
    }

    setLocalError(undefined)

    if (!isCsv(file)) {
      setLocalError(WRONG_TYPE_MESSAGE)
      return
    }

    if (file.size > IMPORT_MAX_INLINE_BYTES) {
      setLocalError(TOO_BIG_MESSAGE)
      return
    }

    setReading(true)
    pendingFile.current = { name: file.name, size: file.size }

    try {
      const csv = await file.text()
      await runImportColumns({ variables: { entity, source: { csv } } })
    } catch {
      setReading(false)
      setLocalError('That file could not be read. Try again.')
    }
  }

  function handleRejected(files: File[]) {
    const file = files?.[0]

    if (file && !isCsv(file)) {
      setLocalError(WRONG_TYPE_MESSAGE)
      return
    }
    if (file && file.size > IMPORT_MAX_INLINE_BYTES) {
      setLocalError(TOO_BIG_MESSAGE)
      return
    }

    // Any other rejection reason (a multi-file drop, for example).
    setLocalError('That file could not be used. Choose a single CSV file.')
  }

  const serverError = error ? getImportErrorMessage(error) : undefined
  const message = localError ?? serverError

  return (
    <div className={classes.root}>
      {message && (
        <Alert severity="error">
          {message}
        </Alert>
      )}

      <Dropzone
        className={classnames(classes.dropzone, { [classes.dropzoneDisabled]: loading })}
        accept={CSV_ACCEPT}
        multiple={false}
        maxSize={IMPORT_MAX_INLINE_BYTES}
        disabled={loading}
        onDrop={handleDrop}
        onDropRejected={handleRejected}
        shouldRenderPreview
        renderPreview={() => (
          <div className={classes.preview}>
            {loading ? (
              <>
                <Spinner />
                <Typography variant="body1">Reading your file...</Typography>
              </>
            ) : (
              <>
                <div className={classes.iconTile}>
                  <UploadIcon className={classes.icon} />
                </div>
                <Typography variant="body1" className={classes.prompt}>
                  Drop your CSV here, or <span className={classes.browse}>browse</span>
                </Typography>
                <Typography variant="body2" className={classes.helper}>
                  CSV files up to {IMPORT_MAX_INLINE_LABEL}. Header row required.
                </Typography>
              </>
            )}
          </div>
        )}
      />
    </div>
  )
}
